import { Routes, Route, Navigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { useEffect } from 'react';

import { useAuthStore } from './store/authStore';
import LandingPage from './pages/LandingPage';
import DashboardLayout from './components/layout/DashboardLayout';
import DashboardPage from './pages/DashboardPage';
import FundsPage from './pages/FundsPage';
import FundExplorerPage from './pages/FundExplorerPage';
import RiskAnalysisPage from './pages/RiskAnalysisPage';
import RegulatoryPage from './pages/RegulatoryPage';
import FundAnalyticsPage from './pages/FundAnalyticsPage';
import ReportsPage from './pages/ReportsPage';
import AdminPage from './pages/AdminPage';
import DataSourcesPage from './pages/DataSourcesPage';
import AIMLConfigPage from './pages/AIMLConfigPage';
import AIConfigPage from './pages/AIConfigPage';
import RegulatoryIntelligencePage from './pages/RegulatoryIntelligencePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ProfilePage from './pages/ProfilePage';
import SettingsPage from './pages/SettingsPage';
import AlertsPage from './pages/AlertsPage';
import WorldCheckPage from './pages/WorldCheckPage';
import EnhancedRiskAnalytics from './pages/EnhancedRiskAnalytics';
import AutomatedDocumentProcessing from './pages/AutomatedDocumentProcessing';
import RegulatoryCalendar from './pages/RegulatoryCalendar';
import CashPositionsPage from './pages/CashPositionsPage';
import PositionBreaksPage from './pages/PositionBreaksPage';
import ShareClassesPage from './pages/ShareClassesPage';
import InvestorsPage from './pages/InvestorsPage';
import TransactionsPage from './pages/TransactionsPage';
import NAVWorkflowPage from './pages/NAVWorkflowPage';
import ApprovalsPage from './pages/ApprovalsPage';
import KIDManagementPage from './pages/KIDManagementPage';
import DepositaryPage from './pages/DepositaryPage';
import AMLScreeningPage from './pages/AMLScreeningPage';
import ProtectedRoute from './components/auth/ProtectedRoute';
import PrivacyPolicyPage from './pages/legal/PrivacyPolicyPage';
import CookiePolicyPage from './pages/legal/CookiePolicyPage';
import TermsOfServicePage from './pages/legal/TermsOfServicePage';
import LegalNoticePage from './pages/legal/LegalNoticePage';
import AboutPage from './pages/legal/AboutPage';
import AccessibilityPage from './pages/legal/AccessibilityPage';
import DataProcessingPage from './pages/legal/DataProcessingPage';

function App() {
  const { isAuthenticated, initializeAuth, accessToken, user } = useAuthStore();

  // Initialize authentication on app load
  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  // Only consider authenticated if we have BOTH token and user
  // This prevents stale localStorage from granting access
  const isReallyAuthenticated = isAuthenticated && accessToken && user;

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Routes>
        {/* Public routes */}
        <Route
          path="/"
          element={
            isReallyAuthenticated ? <Navigate to="/dashboard" replace /> : <LandingPage />
          }
        />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/cookies" element={<CookiePolicyPage />} />
        <Route path="/terms" element={<TermsOfServicePage />} />
        <Route path="/legal" element={<LegalNoticePage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/accessibility" element={<AccessibilityPage />} />
        <Route path="/data-processing" element={<DataProcessingPage />} />

        {/* Protected routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<DashboardPage />} />
          <Route path="funds" element={<FundsPage />} />
          <Route path="fund-explorer" element={<FundExplorerPage />} />
          <Route path="cash-positions" element={<CashPositionsPage />} />
          <Route path="position-breaks" element={<PositionBreaksPage />} />
          <Route path="risk-analysis" element={<RiskAnalysisPage />} />
          <Route path="enhanced-risk-analytics" element={<EnhancedRiskAnalytics />} />
          <Route path="document-processing" element={<AutomatedDocumentProcessing />} />
          <Route path="regulatory-calendar" element={<RegulatoryCalendar />} />
          <Route path="fund-analytics" element={<FundAnalyticsPage />} />
          <Route path="regulatory" element={<RegulatoryPage />} />
          <Route path="reports" element={<ReportsPage />} />
          <Route path="admin" element={<AdminPage />} />
          <Route path="data-sources" element={<DataSourcesPage />} />
          <Route path="ai-ml" element={<AIMLConfigPage />} />
          <Route path="ai-config" element={<AIConfigPage />} />
          <Route path="regulatory-intelligence" element={<RegulatoryIntelligencePage />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="alerts" element={<AlertsPage />} />
          <Route path="worldcheck" element={<WorldCheckPage />} />
          <Route path="share-classes" element={<ShareClassesPage />} />
          <Route path="investors" element={<InvestorsPage />} />
          <Route path="transactions" element={<TransactionsPage />} />
          <Route path="nav-workflow" element={<NAVWorkflowPage />} />
          <Route path="approvals" element={<ApprovalsPage />} />
          <Route path="kid-management" element={<KIDManagementPage />} />
          <Route path="depositary" element={<DepositaryPage />} />
          <Route path="aml-screening" element={<AMLScreeningPage />} />
        </Route>

        {/* Catch all route */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Box>
  );
}

export default App;
